import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";

type LogActivityInput = {
  title: string;
  description?: string;
  categorySlug?: string;
  screenSlug?: string;
  entitySlug?: string;
  operationSlug?: string;
  metadata?: Record<string, any>;
  tenantId?: string;
};

const categoryCache = new Map<string, string>();

async function resolveCategoryId(slug?: string) {
  if (!slug) return null;
  const cached = categoryCache.get(slug);
  if (cached) return cached;
  const { data, error } = await supabase.from("category_activities" as any).select("id").eq("slug", slug).maybeSingle();
  if (error) return null;
  const id = data?.id ?? null;
  if (id) categoryCache.set(slug, id);
  return id;
}

async function getSessionContext() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { tenantId: null as string | null, profileId: null as string | null };
  const { data: profile, error } = await supabase.from("profiles" as any).select("tenant_id").eq("id", user.id).maybeSingle();
  if (error) return { tenantId: null, profileId: user.id };
  return { tenantId: profile?.tenant_id ?? null, profileId: user.id };
}

export async function logActivity(input: LogActivityInput) {
  const ctx = input.tenantId ? { tenantId: input.tenantId } : await getSessionContext();
  if (!ctx.tenantId) return;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const categoryId = await resolveCategoryId(input.categorySlug);
  const screenId = await resolveCategoryId(input.screenSlug);
  const entityId = await resolveCategoryId(input.entitySlug);
  const operationId = await resolveCategoryId(input.operationSlug);
  const payload = {
    tenant_id: ctx.tenantId,
    title: input.title,
    description: input.description ?? null,
    category_id: categoryId,
    category_screen_id: screenId,
    category_entity_id: entityId,
    category_operation_id: operationId,
    created_by: user.id,
    metadata: input.metadata ?? null,
  };
  await (supabase as any).from("activities").insert(payload);
}

export async function logCrud(params: { entitySlug: string; operationSlug: string; entityId?: string; entityName?: string; screenSlug?: string; metadata?: Record<string, any>; before?: Record<string, any> | null; after?: Record<string, any> | null; }) {
  const op = params.operationSlug.replace(/^operation:/, "");
  const entitySlug = params.entitySlug.startsWith("entity:") ? params.entitySlug : `entity:${params.entitySlug}`;
  const opPt = (() => {
    switch (op) {
      case "create": return "criado";
      case "update": return "atualizado";
      case "delete": return "excluído";
      case "open": return "aberto";
      case "close": return "fechado";
      case "import": return "importado";
      case "export": return "exportado";
      default: return op;
    }
  })();
  const entityLabelPt = (() => {
    const t = entitySlug.replace("entity:", "");
    switch (t) {
      case "products": return "Produtos";
      case "clients": return "Clientes";
      case "suppliers": return "Fornecedores";
      case "product_categories": return "Categorias de Produto";
      case "expense_categories": return "Categorias de Despesa";
      case "forms": return "Formulários";
      case "knowledge_bases": return "Bases de Conhecimento";
      case "kb_documents": return "Documentos";
      case "bank_accounts": return "Contas Bancárias";
      case "cash_register_sessions": return "Sessões de Caixa";
      case "transactions": return "Transações";
      case "subscription_coupons": return "Cupons";
      case "subscription_transactions": return "Transações de Assinatura";
      case "profiles": return "Usuários";
      case "roles": return "Perfis";
      case "permissions": return "Permissões";
      case "tenants": return "Empresa";
      case "api_keys": return "Chaves de API";
      case "form_assignments": return "Atribuições de Formulário";
      case "payment_settlement_configs": return "Configurações de Recebimento";
      default: return t;
    }
  })();
  const title = params.entityName ? `${params.entityName} ${opPt}` : `Atualização em ${entityLabelPt}`;
  const description = undefined;
  const link_path = inferLinkPath(entitySlug, op, params.entityId);
  const changes = op === "update" ? computeDiff(params.before, params.after) : undefined;
  await logActivity({
    title,
    description,
    categorySlug: `category:${entitySlug.replace("entity:", "")}`,
    entitySlug,
    operationSlug: `operation:${op}`,
    screenSlug: params.screenSlug ? `screen:${params.screenSlug}` : undefined,
    metadata: { ...(params.metadata || {}), entity_table: entitySlug.replace("entity:", ""), entity_id: params.entityId, link_path, changes },
  });
}

export async function logAuth(type: "login" | "logout", method?: string) {
  await logActivity({
    title: type === "login" ? "Login" : "Logout",
    description: method ? `${type} via ${method}` : undefined,
    categorySlug: "category:security",
    operationSlug: `operation:${type}`,
    metadata: method ? { method } : undefined,
  });
}

export async function logNavigation(path: string, screenSlug?: string) {
  await logActivity({
    title: "Navegação",
    description: path,
    categorySlug: "category:saas",
    operationSlug: "operation:navigate",
    screenSlug: screenSlug ? `screen:${screenSlug}` : undefined,
    metadata: { path, link_path: path },
  });
}

export async function markActivitiesRead(ids: string[]) {
  await (supabase as any).from("activities").update({ read_at: (supabase as any).rpc ? undefined : undefined }).in("id", ids);
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  const { error } = await (supabase as any).rpc("mark_activities_read", { ids });
  if (error) {
    await (supabase as any).from("activities").update({ read_at: (supabase as any).sql ? undefined : undefined }).in("id", ids);
  }
}

export function useTenantId() {
  const { tenantId } = useTenant();
  return tenantId;
}
function inferLinkPath(entitySlug?: string | null, operationSlug?: string | null, id?: string | null) {
  const entity = (entitySlug || "").replace(/^entity:/, "");
  switch (entity) {
    case "products":
      return id ? `/produto/${id}` : "/produtos";
    case "clients":
      return id ? `/cliente/${id}` : "/clientes";
    case "suppliers":
      return id ? `/fornecedor/${id}` : "/fornecedores";
    case "product_categories":
      return "/categorias";
    case "forms":
      return id ? `/configuracoes/formularios/${id}` : "/configuracoes/formularios";
    case "knowledge_bases":
      return "/configuracoes/rag-bases";
    case "kb_documents":
      return "/configuracoes/rag-documentos";
    case "bank_accounts":
      return "/configuracoes/contas-bancarias";
    case "roles":
      return "/perfis";
    case "permissions":
      return "/permissoes";
    case "profiles":
      return id ? `/usuarios/${id}` : "/usuarios";
    case "tenants":
      return id ? `/tenants/${id}` : "/clientes-saas";
    case "cash_register_sessions":
      return "/caixa";
    case "transactions":
      return "/pdv";
    case "subscription_coupons":
      return "/cupons";
    case "subscription_transactions":
      return "/transacoes";
    default:
      return "/";
  }
}

function computeDiff(before: Record<string, any> | null | undefined, after: Record<string, any> | null | undefined) {
  const changes: Record<string, { before: any; after: any }> = {};
  if (!before || !after) return changes;
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const EXCLUDED = new Set(["updated_at", "updatedAt", "updated_by", "updatedBy"]);
  for (const k of keys) {
    if (EXCLUDED.has(k)) continue;
    const b = before[k];
    const a = after[k];
    if (typeof a === "undefined") continue;
    const isScalar = (v: any) => v == null || typeof v === "string" || typeof v === "number" || typeof v === "boolean";
    if (isScalar(b) && isScalar(a) && b !== a) {
      changes[k] = { before: b, after: a };
    }
  }
  return changes;
}
